import styled from 'styled-components';
import { IconSizeType, SvgFileNameType } from '../../../interfaces/Element.interfaces';
import { Icon } from '../icon/Icon';

// TODO : Button 컴포넌트의 미구현된 기능 (Loading, Tooltip) 구현 https://gint-team.atlassian.net/browse/PVM-1770

const buttonColors = {
  primary: { background: '#445EFF', text: '#FFFFFF', outline: 'none' },
  secondary: { background: '#A7A9B6', text: '#FFFFFF', outline: 'none' },
  outlined: { background: '#FFFFFF', text: '#445EFF', outline: '#445EFF' },
  naked: { background: '#FFFFFF', text: '#858894', outline: 'none' },
  light: { background: '#F2F2F7', text: '#858894', outline: 'none' },
  heavy: { background: '#505363', text: '#FFFFFF', outline: 'none' },
  warning: { background: '#FF3049', text: '#FFFFFF', outline: 'none' },
  success: { background: '#34C77E', text: '#FFFFFF', outline: 'none' },
} as const;

const buttonSizes = {
  xs: {
    height: '22px',
    padding: '8px',
    gap: '2px',
  },
  sm: {
    height: '32px',
    padding: '16px',
    gap: '4px',
  },
  md: {
    height: '40px',
    padding: '16px',
    gap: '4px',
  },
} as const;

const buttonTextSizes = {
  sm: '12px',
  md: '14px',
  lg: '18px',
} as const;

type ButtonColorType = keyof typeof buttonColors;
type ButtonSizeType = keyof typeof buttonSizes;
type ButtonTextSizeType = keyof typeof buttonTextSizes;
type ButtonBorderType = 'square' | 'round';

interface IButtonIcon {
  position: 'left' | 'right';
  size: IconSizeType;
  fileName: SvgFileNameType;
  fill?: string;
}

interface ButtonProps {
  icon?: IButtonIcon;
  borderType: ButtonBorderType;
  isDisabled?: boolean;
  color: ButtonColorType;
  size: ButtonSizeType;
  text: {
    size: ButtonTextSizeType;
    content: string;
  };
  onClick: () => void;
}

export const Button = ({
  icon,
  borderType,
  isDisabled = false,
  color,
  size,
  text,
  onClick,
}: ButtonProps): JSX.Element => {
  const ButtonIcon: JSX.Element = icon ? <Icon name={icon.fileName} size={icon.size} fill={icon.fill} /> : <></>;

  return (
    <ColoredButton
      disabled={isDisabled}
      borderType={borderType}
      color={color}
      size={size}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {icon && icon.position === 'left' && ButtonIcon}
      <ButtonText color={color} textSize={text.size}>
        {text.content}
      </ButtonText>
      {icon && icon.position === 'right' && ButtonIcon}
    </ColoredButton>
  );
};

const ColoredButton = styled.button<{
  borderType: ButtonBorderType;
  color: ButtonColorType;
  size: ButtonSizeType;
}>`
  height: ${(props) => buttonSizes[props.size].height};
  max-height: ${(props) => buttonSizes[props.size].height};

  padding: ${(props) => buttonSizes[props.size].padding};

  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  gap: ${(props) => buttonSizes[props.size].gap};

  border-radius: ${(props) => (props.borderType === 'square' ? '8px' : props.borderType === 'round' ? '80px' : '0px')};

  background-color: ${(props) => buttonColors[props.color].background};

  ${(props) =>
    buttonColors[props.color].outline !== 'none' && `border: 1px solid ${buttonColors[props.color].outline};`}

  &:disabled {
    opacity: 0.3;
  }
`;

const ButtonText = styled.span<{ color: ButtonColorType; textSize: ButtonTextSizeType }>`
  color: ${(props) => buttonColors[props.color].text};

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: ${(props) => buttonTextSizes[props.textSize]};
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;
